/**
 * The rules every outbound e-label request follows, as pure functions the edge relay, the edge
 * verifier and the tests share: who may be called, how redirects are followed, what a body may be.
 */
import type { ELabelProvider, ELabelUserAgentClass } from "./types.js";

/** Redirects followed per request: a chain longer than this is a loop or an attack, not a label. */
export const ELABEL_MAX_HOPS = 6;
/** Overall time for one relayed request including every hop. */
export const ELABEL_FETCH_TIMEOUT_MS = 8000;
/** Largest body the relay reads; a real provider page is a few tens of KB. */
export const ELABEL_MAX_BODY_BYTES = 1_500_000;

export const ELABEL_REDIRECT_STATUSES: ReadonlySet<number> = new Set([301, 302, 303, 307, 308]);

/**
 * `honest` says who we are. `mobile` is for a server that serves a phone-only page and answers
 * anything else with a stub; it is a plain mobile browser string, disclosed in docs/elabel-providers.md.
 */
export const ELABEL_USER_AGENTS: Readonly<Record<ELabelUserAgentClass, string>> = {
  honest: "proef-elabel/1 (+https://proef.sevenwoods.nl)",
  mobile: "Mozilla/5.0 (Android 14; Mobile; rv:128.0) Gecko/128.0 Firefox/128.0",
};

/** Headers a relayed request carries: who asks, what it accepts and the language order; never a cookie or a referrer. */
export function elabelRequestHeaders(provider: ELabelProvider): Record<string, string> {
  const languages = provider.needs.languages ?? ["nl", "en"];
  return {
    "user-agent": ELABEL_USER_AGENTS[provider.needs.userAgent],
    accept: "text/html,application/json;q=0.9,text/plain;q=0.8,*/*;q=0.5",
    "accept-language": languages
      .map((lang, i) => (i === 0 ? lang : `${lang};q=${(1 - i * 0.1).toFixed(1)}`))
      .join(","),
  };
}

export function parseUrl(text: string): URL | undefined {
  try {
    return new URL(text.trim());
  } catch {
    return undefined;
  }
}

/**
 * The scanned text with an explicit port dropped, so a bottle QR printed with e.g. `:8444` is read
 * as the same link on the scheme's default port; the odd port is never contacted. Anything
 * unparseable, non-http(s) or carrying credentials comes back unchanged (and stays refused).
 */
export function withDefaultPort(text: string): string {
  const url = parseUrl(text);
  if (url === undefined || (url.protocol !== "https:" && url.protocol !== "http:")) return text;
  if (url.username !== "" || url.password !== "") return text;
  // WHATWG URL already hides a scheme-default port, so a remaining one is non-default.
  if (url.port === "") return text;
  url.port = "";
  return url.href;
}

/** True when `text` is an http(s) URL carrying an explicit non-default port. */
export function hasExplicitPort(text: string): boolean {
  const url = parseUrl(text);
  return (
    url !== undefined && (url.protocol === "https:" || url.protocol === "http:") && url.port !== ""
  );
}

/** https, an exact allow-listed host, the default port and no credentials: nothing else is ever requested. */
export function isAllowedUrl(url: URL, hosts: readonly string[]): boolean {
  return (
    url.protocol === "https:" &&
    url.port === "" &&
    url.username === "" &&
    url.password === "" &&
    hosts.includes(url.hostname)
  );
}

/** The URL without its fragment, which a server never sees: the identity of a page for loop detection. */
export function withoutFragment(url: URL): string {
  const copy = new URL(url.href);
  copy.hash = "";
  return copy.href;
}

export type HopRefusal = "bad-location" | "off-allowlist" | "loop" | "too-many-hops";

/**
 * Where a redirect leads, or why it must not be followed. A relative `Location` is resolved
 * against the page that sent it. `visited` holds every URL requested so far (fragment-free,
 * the current one last), so a revisit is a loop and the count of followed hops is bounded.
 */
export function nextHop(
  provider: ELabelProvider,
  current: URL,
  location: string | null,
  visited: readonly string[],
): { url: URL } | { refused: HopRefusal } {
  if (location === null || location.trim() === "") return { refused: "bad-location" };
  let next: URL;
  try {
    next = new URL(location, current);
  } catch {
    return { refused: "bad-location" };
  }
  if (!isAllowedUrl(next, provider.hosts)) return { refused: "off-allowlist" };
  if (visited.length > ELABEL_MAX_HOPS) return { refused: "too-many-hops" };
  if (visited.includes(withoutFragment(next))) return { refused: "loop" };
  return { url: next };
}

/** A body the relay will hand to a phone as text: text, JSON or XML, never an image, a PDF or a download. */
export function isTextContentType(contentType: string): boolean {
  const type = (contentType.split(";")[0] ?? "").trim().toLowerCase();
  return (
    type.startsWith("text/") ||
    type === "application/json" ||
    type === "application/xml" ||
    type === "application/xhtml+xml" ||
    type.endsWith("+json") ||
    type.endsWith("+xml")
  );
}
